import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const normalizeRole = (role) =>
  (role || '').toUpperCase().replace(/\s+/g, '_').replace(/-+/g, '_');

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, activeRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to appropriate login page
    const isSuperAdminRoute = location.pathname.startsWith('/superadmin');
    return <Navigate to={isSuperAdminRoute ? '/superadmin/login' : '/login'} state={{ from: location }} replace />;
  }

  // Use activeRole if available, fallback to user.role
  const userRole = normalizeRole(activeRole || user.role);
  const isSuperAdminUser = normalizeRole(user.role) === 'SUPER_ADMIN' || user.role_id === 'SUPER_ADMIN';

  if (allowedRoles && allowedRoles.length > 0) {
    const normalizedAllowed = allowedRoles.map((r) =>
      (r || '').toUpperCase().replace(/\s+/g, '_').replace(/-+/g, '_')
    );

    const isAllowed = normalizedAllowed.includes(userRole);

    if (!isAllowed) {
      if (isSuperAdminUser && location.pathname.startsWith('/superadmin')) {
        return <Navigate to="/superadmin/companies" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check if company setup is required
  const isAdminOrHR = ['COMPANY_ADMIN', 'HR_MANAGER'].includes(userRole);
  if (isAdminOrHR && user.company_setup_complete === false && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }

  // If already at setup but setup is complete, redirect to dashboard
  if (isAdminOrHR && user.company_setup_complete === true && location.pathname === '/setup') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    const userRole = normalizeRole(user.role);
    const isSuperAdminUser = userRole === 'SUPER_ADMIN' || user.role_id === 'SUPER_ADMIN';
    if (isSuperAdminUser) {
      return <Navigate to="/superadmin/companies" replace />;
    }

    const isAdminOrHR =
      ['COMPANY_ADMIN', 'HR_MANAGER'].includes(userRole) ||
      (user.role_id && userRole !== 'EMPLOYEE');

    if (isAdminOrHR && user.company_setup_complete === false) {
      return <Navigate to="/setup" replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  return children;
};
